import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp, HelpCircle, CreditCard, FileText, Brain, Target, Download, Users, Shield } from 'lucide-react';

const HelpFAQ = ({ isOpen, onClose }) => {
  const [openSections, setOpenSections] = useState(new Set(['general']));

  const toggleSection = (sectionId) => {
    setOpenSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const faqData = {
    general: {
      title: "General Questions",
      icon: HelpCircle,
      questions: [
        {
          question: "What is Rastor AI?",
          answer: "Rastor AI is an AI-powered career consulting platform that helps you discover personalized career paths, understand skill-job market alignment, and get step-by-step learning roadmaps for career growth. We use advanced AI to analyze your profile and suggest the best career paths for you."
        },
        {
          question: "How does the AI career analysis work?",
          answer: "Our AI analyzes your skills, experience, education, and goals to understand your background. It then matches you with relevant career paths based on market demand, salary potential, and your personal preferences. The AI considers factors like industry trends, skill requirements, and growth opportunities."
        },
        {
          question: "Is my data secure?",
          answer: "Yes, we take data security seriously. Your profile information is encrypted and stored securely. We use industry-standard security practices and never share your personal information with third parties. You can also delete your data at any time."
        },
        {
          question: "Do I need to create an account?",
          answer: "No, you can use Rastor AI without creating an account. However, creating an account allows you to save your roadmaps, track progress, and access your learning history across devices."
        }
      ]
    },
    payment: {
      title: "Payment & Pricing",
      icon: CreditCard,
      questions: [
        {
          question: "How much does Rastor AI cost?",
          answer: "Rastor AI offers flexible subscription plans starting from ₹0 (Free) to ₹2,999/month (Enterprise). The Free plan includes basic career analysis, while paid plans offer unlimited roadmaps, advanced analytics, mentorship, and more. Get up to 50% OFF with our limited time offer! Cancel anytime with no hidden fees."
        },
        {
          question: "What's included in the free preview?",
          answer: "The free preview includes one complete career path analysis with match percentage, reasoning, salary information, and a basic roadmap preview. You can see exactly what you'll get before making a purchase."
        },
        {
          question: "What payment methods do you accept?",
          answer: "We accept all major credit cards, debit cards, UPI, and net banking through our secure payment partner Cashfree. All payments are processed securely and you'll receive a confirmation email."
        },
        {
          question: "Is there a money-back guarantee?",
          answer: "Yes, we offer a 30-day money-back guarantee. If you're not satisfied with your career analysis or roadmap, contact our support team within 30 days for a full refund."
        }
      ]
    },
    features: {
      title: "Features & Roadmaps",
      icon: Target,
      questions: [
        {
          question: "How accurate are the career suggestions?",
          answer: "Our AI uses advanced algorithms trained on current job market data, industry trends, and career progression patterns. The suggestions are based on real market data and are updated regularly to reflect current industry demands."
        },
        {
          question: "What's included in the learning roadmaps?",
          answer: "Each roadmap includes week-by-week learning schedules, curated resources (courses, books, tools), practical projects, skill assessments, and progress tracking. The roadmaps are personalized based on your timeline and learning preferences."
        },
        {
          question: "Can I download my roadmap?",
          answer: "Yes! After purchase, you can download your complete roadmap as a beautiful PDF document. This includes all learning materials, resources, and your personalized schedule."
        },
        {
          question: "How long does it take to complete a roadmap?",
          answer: "Roadmap duration varies based on your chosen career path and timeline preferences. Most roadmaps are designed for 3-12 months, but you can customize the timeline during the personalization process."
        }
      ]
    },
    technical: {
      title: "Technical Support",
      icon: Brain,
      questions: [
        {
          question: "What file formats are supported for resume upload?",
          answer: "We currently support PDF files for resume uploads. Make sure your PDF is clear and text-readable for the best results. We're working on supporting more formats in the future."
        },
        {
          question: "Why isn't my resume being parsed correctly?",
          answer: "Resume parsing works best with well-formatted PDFs that have clear text. If you're having issues, try uploading a different version of your resume or use the manual profile creation option instead."
        },
        {
          question: "How do I track my learning progress?",
          answer: "After purchasing, you'll get access to your personalized dashboard where you can mark tasks as complete, track your progress percentage, and see your learning achievements."
        },
        {
          question: "Can I access my roadmaps on mobile?",
          answer: "Yes! Rastor AI is fully responsive and works great on mobile devices. You can access your roadmaps, track progress, and download PDFs from any device."
        }
      ]
    },
    support: {
      title: "Customer Support",
      icon: Users,
      questions: [
        {
          question: "How can I contact customer support?",
          answer: "You can reach our support team at support@rastorai.com. We typically respond within 24 hours. For urgent payment issues, please include your order ID in your email."
        },
        {
          question: "What information should I include in support emails?",
          answer: "Please include your email address, order ID (if applicable), and a detailed description of your issue. Screenshots are helpful for technical problems."
        },
        {
          question: "Do you offer refunds?",
          answer: "Yes, we offer a 30-day money-back guarantee. If you're not satisfied with your purchase, contact us within 30 days for a full refund. No questions asked."
        },
        {
          question: "Can I get help with my career decisions?",
          answer: "While our AI provides excellent career guidance, we recommend consulting with career counselors for major life decisions. Our roadmaps are educational tools to help you learn new skills."
        }
      ]
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Help & FAQ</h2>
              <p className="text-slate-400 text-sm">Find answers to common questions about Rastor AI</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {Object.entries(faqData).map(([sectionId, section]) => {
            const IconComponent = section.icon;
            const isOpen = openSections.has(sectionId);
            
            return (
              <div key={sectionId} className="bg-slate-700/50 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleSection(sectionId)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-600/50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <IconComponent className="w-5 h-5 text-emerald-400" />
                    <span className="font-semibold text-white">{section.title}</span>
                  </div>
                  {isOpen ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
                
                {isOpen && (
                  <div className="border-t border-slate-600">
                    {section.questions.map((faq, index) => (
                      <div key={index} className="p-4 border-b border-slate-600 last:border-b-0">
                        <h4 className="font-semibold text-white mb-2">{faq.question}</h4>
                        <p className="text-slate-300 text-sm leading-relaxed">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-700">
          <div className="bg-gradient-to-r from-emerald-900/50 to-teal-900/50 border border-emerald-700/50 rounded-xl p-4">
            <h3 className="font-semibold text-emerald-400 mb-2">Still need help?</h3>
            <p className="text-slate-300 text-sm mb-3">
              Can't find the answer you're looking for? Our support team is here to help!
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="mailto:support@rastorai.com"
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Contact Support
              </a>
              <span className="text-slate-400 text-sm self-center">
                Response within 24 hours
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpFAQ; 